/**
 * Resolving the per-school Grammar switch.
 *
 * Kept as a pure function, separate from the store getter that calls it, so
 * the fallback rules below are unit-testable. Importing the real school store
 * in a spec drags a chain of view components (and their node_modules CSS and
 * ESM) into jest, which is why every store spec in this repo mocks stores
 * instead of importing them.
 */

type SchoolLike = { settings?: { grammarEnabled?: boolean } } | null | undefined;

// `settings` is typed with an index signature to match SchoolFilterOption,
// whose own settings carries one. Narrowing it to just `grammarEnabled` here
// makes it a TypeScript weak type, and passing the real filter-option object
// then fails with "no properties in common" (TS2559) even though the read
// below is perfectly valid.
type SchoolGroupLike = {
  schools?: Array<{ _id: string; settings?: { [key: string]: unknown } }>;
};

/**
 * Read the flag off a single school-shaped object.
 *
 * `=== true` rather than truthiness: the field is absent on every school that
 * predates it, and the API serves some of these objects from .lean() reads and
 * aggregations where the mongoose default is never applied.
 */
export function schoolHasGrammarEnabled(school: SchoolLike): boolean {
  return school?.settings?.grammarEnabled === true;
}

/**
 * Resolve the flag for the selected school.
 *
 * Prefers the loaded school, then falls back to the copy held in the filter
 * options. The fallback matters because the loaded school arrives from an
 * un-awaited fetch and is null on first paint, while the filter options are
 * populated at login. Reading only one of the two gives a flag that flickers.
 */
export function resolveSchoolGrammarEnabled(
  currentSchool: SchoolLike,
  schoolGroups: SchoolGroupLike[] | undefined,
  selectedSchoolId: string | undefined | null
): boolean {
  if (currentSchool) {
    return schoolHasGrammarEnabled(currentSchool);
  }

  if (!selectedSchoolId) return false;

  for (const group of schoolGroups ?? []) {
    const school = group?.schools?.find((item) => item._id === selectedSchoolId);
    if (school) {
      return school.settings?.grammarEnabled === true;
    }
  }

  return false;
}
